import { is, last, map, reverse } from "ramda";
import { type FC, useMemo } from "react";
import { Item, MenuOptionItem, Select } from "widgets";
import { isUndefinedOrEmptyArray } from "widgets/utils";
import type { StoreApi, UseBoundStore } from "zustand";

import { rootProjectFileData } from "@/modules/files/settings/settings.ts";
import type { FileBrowserStore } from "@/modules/files/stores/file-browser.ts";
import { getLastFilePathElement } from "@/modules/files/utils/file";

export interface BreadcrumbButtonProps {
  useStore: UseBoundStore<StoreApi<FileBrowserStore>>;
}

const ROOT_KEY = "__root__";

const BreadcrumbButton: FC<BreadcrumbButtonProps> = (props) => {
  const breadcrumbs = props.useStore((state) => state.breadcrumbs);
  const loading = props.useStore((state) => state.loading);
  const load = props.useStore((state) => state.load);

  const label = useMemo(() => {
    return !isUndefinedOrEmptyArray(breadcrumbs)
      ? getLastFilePathElement(last(breadcrumbs!)?.humanReadablePath ?? "")
      : loading
        ? ""
        : "/";
  }, [breadcrumbs]);

  const currentFolderKey = useMemo(
    () => (!isUndefinedOrEmptyArray(breadcrumbs) ? (last(breadcrumbs!)?.id ?? ROOT_KEY) : undefined),
    [breadcrumbs],
  );

  return is(Array, breadcrumbs) ? (
    <Select
      width={250}
      style={{ maxWidth: 250 }}
      onSelectionChange={(key) => {
        load(key === ROOT_KEY ? rootProjectFileData.id : Number(key));
      }}
      isDisabled={loading || !breadcrumbs || breadcrumbs.length <= 1}
      placeholder={label}
      selectedKey={String(currentFolderKey)}
    >
      {map((breadcrumb) => {
        const key = breadcrumb.id ?? ROOT_KEY;

        return (
          <Item key={key}>
            <MenuOptionItem
              icon={key === currentFolderKey ? "folder_open" : "folder"}
              title={getLastFilePathElement(breadcrumb.humanReadablePath)}
            />
          </Item>
        );
      }, reverse(breadcrumbs))}
    </Select>
  ) : null;
};

export { BreadcrumbButton };
